/**
 * Secure Environment Configuration
 * Validates and securely handles environment variables
 */

import { secureLogger } from '../utils/secureLogger';

// Define required environment variables
const REQUIRED_ENV_VARS = [
  'REACT_APP_SUPABASE_URL',
  'REACT_APP_SUPABASE_ANON_KEY'
];

// Define optional environment variables with defaults
const OPTIONAL_ENV_VARS = {
  'REACT_APP_API_BASE_URL': process.env.NODE_ENV === 'production' 
    ? 'https://sparvi-webapp-fjdjdvh2bse9d0gm.centralus-01.azurewebsites.net/api' 
    : 'http://localhost:5000/api',
  'REACT_APP_DEBUG_MODE': 'false',
  'REACT_APP_ENVIRONMENT': process.env.NODE_ENV || 'development'
};

// Environment variables that should never be logged
const SENSITIVE_ENV_VARS = [
  'REACT_APP_SUPABASE_ANON_KEY'
];

/**
 * Validate environment variables
 */
function validateEnvironment() {
  const missing = [];
  const invalid = [];

  // Check required variables
  REQUIRED_ENV_VARS.forEach(varName => {
    const value = process.env[varName];
    if (!value) {
      missing.push(varName);
    } else if (varName === 'REACT_APP_SUPABASE_URL' && !isValidUrl(value)) {
      invalid.push(varName);
    }
  });

  if (missing.length > 0) {
    secureLogger.error('Missing required environment variables', { missing });
    throw new Error(`Missing required environment variables: ${missing.join(', ')}`);
  }

  if (invalid.length > 0) {
    secureLogger.error('Invalid environment variables', { invalid });
    throw new Error(`Invalid environment variables: ${invalid.join(', ')}`);
  }

  secureLogger.info('Environment validation passed');
}

/**
 * Validate URL format
 */
function isValidUrl(string) {
  try {
    const url = new URL(string);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
}

/**
 * Get environment variable with security checks
 */
function getEnvVar(name, defaultValue = null) {
  const value = process.env[name] || OPTIONAL_ENV_VARS[name] || defaultValue;
  
  if (!value) {
    secureLogger.warn(`Environment variable ${name} is not set`);
  }

  // Log non-sensitive environment variables for debugging
  if (!SENSITIVE_ENV_VARS.includes(name) && process.env.NODE_ENV !== 'production') {
    secureLogger.debug(`Environment variable loaded`, { name, hasValue: !!value });
  }

  return value;
}

/**
 * Get obfuscated environment variable for logging
 */
function getObfuscatedEnvVar(name) {
  const value = process.env[name];
  if (!value) return null;
  
  if (value.length <= 8) return '***';
  return `${value.substring(0, 4)}***${value.substring(value.length - 4)}`;
}

/**
 * Environment configuration object
 */
class EnvironmentConfig {
  constructor() {
    // Validate environment on initialization
    validateEnvironment();
    
    // Initialize configuration
    this.config = {
      // API Configuration
      apiBaseUrl: getEnvVar('REACT_APP_API_BASE_URL'),
      
      // Supabase Configuration
      supabaseUrl: getEnvVar('REACT_APP_SUPABASE_URL'),
      supabaseAnonKey: getEnvVar('REACT_APP_SUPABASE_ANON_KEY'),
      
      // Application Configuration
      environment: getEnvVar('REACT_APP_ENVIRONMENT'),
      debugMode: getEnvVar('REACT_APP_DEBUG_MODE') === 'true',
      isProduction: process.env.NODE_ENV === 'production',
      isDevelopment: process.env.NODE_ENV === 'development',
      
      // Security Configuration
      enableSecureLogging: true,
      rateLimitRequests: getEnvVar('REACT_APP_RATE_LIMIT_REQUESTS', '1000'),
      rateLimitWindow: getEnvVar('REACT_APP_RATE_LIMIT_WINDOW', '60000')
    };

    // Validate configuration
    this.validateConfig();
    
    // Log configuration (without sensitive data)
    this.logConfiguration();
  }

  validateConfig() {
    // Ensure HTTPS in production
    if (this.config.isProduction) {
      if (!this.config.supabaseUrl.startsWith('https://')) {
        throw new Error('Supabase URL must use HTTPS in production');
      }
      
      if (!this.config.apiBaseUrl.startsWith('https://')) {
        secureLogger.warn('API Base URL should use HTTPS in production');
      }
    }

    // Validate rate limiting configuration
    const rateLimitRequests = parseInt(this.config.rateLimitRequests);
    const rateLimitWindow = parseInt(this.config.rateLimitWindow);
    
    if (isNaN(rateLimitRequests) || rateLimitRequests <= 0) {
      throw new Error('Invalid rate limit requests configuration');
    }
    
    if (isNaN(rateLimitWindow) || rateLimitWindow <= 0) {
      throw new Error('Invalid rate limit window configuration');
    }
  }

  logConfiguration() {
    const safeConfig = {
      apiBaseUrl: this.config.apiBaseUrl,
      supabaseUrl: this.config.supabaseUrl,
      supabaseAnonKey: getObfuscatedEnvVar('REACT_APP_SUPABASE_ANON_KEY'),
      environment: this.config.environment,
      debugMode: this.config.debugMode,
      isProduction: this.config.isProduction,
      rateLimitRequests: this.config.rateLimitRequests,
      rateLimitWindow: this.config.rateLimitWindow
    };

    secureLogger.info('Application configuration loaded', safeConfig);
  }

  get(key) {
    return this.config[key];
  }

  // Security check methods
  isSecureContext() {
    return this.config.isProduction ? window.isSecureContext : true;
  }

  shouldLogDebug() {
    return this.config.debugMode && !this.config.isProduction;
  }
}

// Create singleton instance
const environmentConfig = new EnvironmentConfig();

// Export configuration
export default environmentConfig;

// Export specific values for backward compatibility
export const {
  apiBaseUrl,
  supabaseUrl,
  supabaseAnonKey,
  environment,
  debugMode,
  isProduction,
  isDevelopment
} = environmentConfig.config;